// src/hooks/useSEO.js - Enhanced SEO hook with robust canonical URL management
import { useEffect, useCallback } from 'react';

export const useSEO = ({
  title = "Connect & Co - International Community Building Hub | No Strangers. Just Remarkable People",
  description = "A global community that helps people form meaningful friendships through intimate gatherings and intentional experiences. ConnectCo is a community-building hub that helps individuals forge authentic friendships through curated Swedish Fika experiences.'",
  keywords = "fika, curated fika, speed fika, monthly fika, weekly fika, international community, intentional experiences, shared experiences, networking events, swedish fika, monthly fika, weekly fika, swedish fika tokyo, weekly fika events, monthly fika meetup, fika coffee tradition, authentic fika experience, fika meetup group, stockholm fika tradition, scandinavian culture tokyo, international coffee meetup, mys eveneing social, forest walks, meaningful friendships, global community, cultural experiences, priority access, community building",
  image = "https://connectco.app/og-image.jpg",
  url = "https://connectco.app",
  canonical,
  type = "website",
  structuredData,
  noindex = false,
  priority = 1.0
}) => {
  
  // Create a robust canonical URL - this is the CRITICAL FIX
  const getCanonicalUrl = useCallback(() => {
    // Priority order: explicit canonical > url prop > current location
    if (canonical) return canonical;
    if (url) return url;
    
    // Fallback: construct from current location
    const currentUrl = new URL(window.location.href);
    // Remove search params and hash for clean canonical
    return `${currentUrl.protocol}//${currentUrl.host}${currentUrl.pathname}`.replace(/\/$/, '') || 'https://connectco.app';
  }, [canonical, url]);

  // Helper function to update or create meta tag
  const updateMetaTag = useCallback((property, content, isProperty = false) => {
    if (!content) return; // Skip empty content
    
    const selector = isProperty ? `meta[property="${property}"]` : `meta[name="${property}"]`;
    let element = document.querySelector(selector);
    
    if (element) {
      element.setAttribute('content', content);
    } else {
      element = document.createElement('meta');
      if (isProperty) {
        element.setAttribute('property', property);
      } else {
        element.setAttribute('name', property);
      }
      element.setAttribute('content', content);
      document.head.appendChild(element);
    }
  }, []);

  // Helper function to update or create link tag  
  const updateLinkTag = useCallback((rel, href, attributes = {}) => {
    if (!href) return; // Skip empty href
    
    let element = document.querySelector(`link[rel="${rel}"]`);
    
    if (element) {
      element.setAttribute('href', href);
      // Update additional attributes
      Object.entries(attributes).forEach(([key, value]) => {
        if (value) element.setAttribute(key, value);
      });
    } else {
      element = document.createElement('link');
      element.setAttribute('rel', rel);
      element.setAttribute('href', href);
      // Add additional attributes
      Object.entries(attributes).forEach(([key, value]) => {
        if (value) element.setAttribute(key, value);
      });
      document.head.appendChild(element);
    }
  }, []);

  // Enhanced structured data injection
  const injectStructuredData = useCallback((data) => {
    if (!data) return;
    
    // Remove existing structured data for this page
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-seo-dynamic="true"]');
    existingScripts.forEach(script => script.remove());

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-dynamic', 'true');
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    // CRITICAL FIX: Set canonical URL IMMEDIATELY and FIRST
    const canonicalUrl = getCanonicalUrl();
    updateLinkTag('canonical', canonicalUrl);
    
    // Set document title
    if (title) {
      document.title = title;
    }

    // Core SEO meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', 'Connect & Co');
    
    // Robots meta tag - handle noindex
    const robotsContent = noindex 
      ? 'noindex, nofollow' 
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
    updateMetaTag('robots', robotsContent);
    
    updateMetaTag('language', 'English');
    updateMetaTag('theme-color', '#9333EA');
    updateMetaTag('application-name', 'Connect & Co');
    updateMetaTag('apple-mobile-web-app-title', 'Connect & Co');
    
    // Geographic meta tags for Tokyo-based community
    updateMetaTag('geo.region', 'JP-13');
    updateMetaTag('geo.placename', 'Tokyo');
    updateMetaTag('geo.position', '35.6762;139.6503');
    updateMetaTag('ICBM', '35.6762, 139.6503');

    // Enhanced Open Graph meta tags
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', canonicalUrl, true); // Use canonical URL
    updateMetaTag('og:site_name', 'Connect & Co', true);
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:image:width', '1200', true);
    updateMetaTag('og:image:height', '630', true);
    updateMetaTag('og:image:alt', 'Connect & Co - International Community Building Hub', true);
    updateMetaTag('og:locale', 'en_US', true);
    
    // Additional OG tags for better social sharing
    updateMetaTag('og:image:type', 'image/jpeg', true);
    updateMetaTag('og:image:secure_url', image, true);
    
    // Article-specific OG tags (for blog posts)
    if (type === 'article') {
      updateMetaTag('og:article:author', 'Connect & Co', true);
      updateMetaTag('og:article:section', 'Community Building', true);
      updateMetaTag('og:article:published_time', new Date().toISOString(), true);
    }

    // Enhanced Twitter meta tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:site', '@connectandco');
    updateMetaTag('twitter:creator', '@connectandco');
    updateMetaTag('twitter:url', canonicalUrl); // Use canonical URL
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
    updateMetaTag('twitter:image:alt', 'Connect & Co - International Community Building Hub');
    
    // Additional Twitter tags
    updateMetaTag('twitter:domain', 'connectco.app');
    updateMetaTag('twitter:label1', 'Community Size');
    updateMetaTag('twitter:data1', '1500+ Members');
    updateMetaTag('twitter:label2', 'Location');
    updateMetaTag('twitter:data2', 'Tokyo & Global');

    // Additional SEO link tags
    // 1-21-26
    //updateLinkTag('alternate', `${canonicalUrl}/feed.xml`, { type: 'application/rss+xml', title: 'Connect & Co Blog RSS Feed' });
    // Only add RSS feed link for blog pages or homepage
    if (url === 'https://connectco.app/blog' || url === 'https://connectco.app') {
      updateLinkTag('alternate', 'https://connectco.app/blog/feed.xml', { 
        type: 'application/rss+xml', 
        title: 'Connect & Co Blog RSS Feed' 
      });
    }

    // Inject structured data if provided
    if (structuredData) {
      injectStructuredData(structuredData);
    }

    // Track URL changes for analytics
    if (typeof gtag !== 'undefined') {
      gtag('config', 'GA_MEASUREMENT_ID', {
        page_title: title,
        page_location: canonicalUrl,
        page_path: new URL(canonicalUrl).pathname
      });
    }

    // Cleanup function
    return () => {
      // Note: We don't remove meta tags on cleanup as they should persist
      // until the next page load or SEO update
    };
  }, [title, description, keywords, image, type, noindex, priority, structuredData, updateMetaTag, updateLinkTag, getCanonicalUrl, injectStructuredData]);

  // Return useful data for components
  return {
    canonicalUrl: getCanonicalUrl(),
    updateMetaTag,
    updateLinkTag,
    injectStructuredData
  };
};

// Enhanced utility functions
export const createCleanUrl = (path, baseUrl = "https://connectco.app") => {
  // Remove trailing slashes and clean the path
  const cleanPath = path.replace(/\/+$/, '').replace(/^\/+/, '');
  return cleanPath ? `${baseUrl}/${cleanPath}` : baseUrl;
};

// Preload critical resources
export const preloadCriticalResources = () => {
  const preloadResource = (href, as, type = null, crossorigin = null) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    if (type) link.type = type;
    if (crossorigin) link.crossOrigin = crossorigin;
    document.head.appendChild(link);
  };

  // Preload critical fonts
  preloadResource('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap', 'style');
  
  // Preload hero image
  preloadResource('https://connectco.app/og-image.jpg', 'image');
};

// Enhanced sitemap generation utilities integrated with your sitemap script
export const generateSitemapData = () => {
  const baseUrl = 'https://connectco.app';
  
  // Static pages matching your sitemap script
  const staticPages = [
    { url: baseUrl, priority: 1.0, changefreq: 'weekly' },
    { url: `${baseUrl}/events`, priority: 0.9, changefreq: 'daily' },
    { url: `${baseUrl}/blog`, priority: 0.8, changefreq: 'weekly' },
    { url: `${baseUrl}/contact`, priority: 0.5, changefreq: 'monthly' },
    { url: `${baseUrl}/privacy`, priority: 0.4, changefreq: 'monthly' },
    { url: `${baseUrl}/terms`, priority: 0.4, changefreq: 'monthly' },
    { url: `${baseUrl}/cookies`, priority: 0.3, changefreq: 'monthly' }
  ];
  
  return staticPages;
};

// Event priorities matching your sitemap script
export const getEventSEOPriority = (eventSlug) => {
  const eventPriorities = {
    // HIGH PRIORITY - Featured/Popular events
    'swedish-fika-january-2025': { priority: 0.9, changefreq: 'weekly' },
    'weekly-swedish-fika-july-2025': { priority: 0.9, changefreq: 'weekly' },
    'ny-pizza-night-daikanyama-july-2025': { priority: 0.85, changefreq: 'weekly' },
    'saturday-happy-hour-namiki667-may-2025': { priority: 0.85, changefreq: 'weekly' },
    
    // MEDIUM-HIGH PRIORITY - Regular events
    'meiji-secret-forest-walk-july-2025': { priority: 0.8, changefreq: 'weekly' },
    'shoot-the-breeze-happy-hour-january-2025': { priority: 0.8, changefreq: 'weekly' },
    
    // STANDARD PRIORITY - General events
    'dawn-zen-forest-walks-january-2025': { priority: 0.75, changefreq: 'weekly' },
    'themed-discussions-february-2025': { priority: 0.75, changefreq: 'weekly' }
  };
  
  return eventPriorities[eventSlug] || { priority: 0.8, changefreq: 'weekly' };
};

// Blog post priorities matching your sitemap script
export const getBlogSEOPriority = (postSlug) => {
  const blogPostPriorities = {
    'swedish-fika-connection-story': { priority: 0.95, changefreq: 'monthly' }, // Cultural foundation article
    'fika-coffee-meeting-story': { priority: 0.95, changefreq: 'monthly' }, // Origin story
    'yanaka-tokyo-authentic-neighborhood': { priority: 0.95, changefreq: 'monthly' } // Authentic Tokyo content
  };
  
  return blogPostPriorities[postSlug] || { priority: 0.7, changefreq: 'monthly' };
};

// Generate robots.txt content
export const generateRobotsTxt = () => {
  return `User-agent: *
Allow: /

# High-priority pages for crawling
Crawl-delay: 1

# Sitemap location
Sitemap: https://connectco.app/sitemap.xml

# Priority crawling hints for important sections
User-agent: Googlebot
Allow: /events/
Allow: /blog/
Crawl-delay: 0

# Block any temporary or admin paths (if they exist)
Disallow: /admin/
Disallow: /temp/
Disallow: /*.json$
Disallow: /*?*

# Social media crawlers
User-agent: facebookexternalhit
Allow: /

User-agent: Twitterbot
Allow: /

User-agent: LinkedInBot
Allow: /`;
};

// Enhanced meta refresh and redirect utilities
export const createMetaRefresh = (url, seconds = 0) => {
  const meta = document.createElement('meta');
  meta.httpEquiv = 'refresh';
  meta.content = `${seconds}; url=${url}`;
  return meta;
};

// Breadcrumb structured data generator
export const generateBreadcrumbSchema = (breadcrumbs) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  };
};

// FAQ structured data generator for events and community
export const generateFAQSchema = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// Common FAQs for Connect & Co
export const connectCoFAQs = [
  {
    question: "How do I get early access to Connect & Co events?",
    answer: "Join our member community to get 24-hour early access to all events before they're published on Meetup. Members skip the waitlist and get priority placement."
  },
  {
    question: "What is Swedish Fika and why is it special?",
    answer: "Swedish Fika is more than just coffee - it's a cultural practice of taking intentional breaks to connect with others. Our Fika events create intimate settings for meaningful conversations and authentic connections."
  },
  {
    question: "Are Connect & Co events suitable for international members?",
    answer: "Absolutely! Our community is designed for international minds. We create cross-cultural experiences where locals, expats, and global travelers connect through shared experiences."
  },
  {
    question: "How many people attend Connect & Co events?",
    answer: "Our events are intentionally intimate, typically hosting 25-30 people for members-only gatherings and up to 50 for public events. This ensures quality connections over quantity."
  },
  {
    question: "What makes Connect & Co different from other networking groups?",
    answer: "We focus on meaningful shared friendships through cultural experiences rather than transactional networking. Our events include Swedish Fika, forest walks, and themed discussions designed for authentic connection."
  }
];

// Event-specific FAQ generator
export const generateEventFAQs = (event) => {
  const baseFAQs = [
    {
      question: `What should I expect at ${event.title}?`,
      answer: event.description + " This is an intimate gathering designed for meaningful connections in a comfortable setting."
    },
    {
      question: "Do I need to be a member to attend?",
      answer: "While some events are open to everyone, members get 24-hour early access and priority placement. Join our community to skip the waitlist."
    },
    {
      question: "What is the dress code?",
      answer: "Smart casual is perfect for most events. We want you to feel comfortable while looking presentable for meeting new people."
    }
  ];

  // Add event-specific FAQs
  if (event.slug?.includes('fika')) {
    baseFAQs.push({
      question: "What is Swedish Fika culture?",
      answer: "Fika is a Swedish concept of taking a coffee break with intention. It's about slowing down, being present, and creating space for genuine conversation and connection."
    });
  }

  if (event.slug?.includes('forest')) {
    baseFAQs.push({
      question: "What should I bring for the forest walk?",
      answer: "Wear comfortable walking shoes and bring water. We'll provide light refreshments. The walks are designed for gentle conversation while enjoying nature."
    });
  }

  return baseFAQs;
};

// Fika-specific FAQ generator
export const generateFikaEventFAQs = (eventType = 'general') => {
  const fikaFAQs = [
    {
      question: "What is Swedish Fika and why should I attend?",
      answer: "Swedish Fika is more than coffee - it's a cultural tradition of slowing down and connecting authentically. Our Fika events create intimate spaces for meaningful conversations in Tokyo's best third-wave cafés."
    },
    {
      question: "What's the difference between Monthly and Weekly Fika?",
      answer: "Monthly Fika is open to everyone and happens the first Sunday of each month. Weekly Signature Fika is members-only, every Tuesday, with smaller groups (20-25 people) for deeper connections."
    },
    {
      question: "How much does Fika cost and what's included?",
      answer: "All Fika events are free to attend. You just pay for your own coffee and pastries (typically ¥500-800). We provide the community, conversation starters, and authentic Swedish Fika atmosphere."
    }
  ];

  if (eventType === 'monthly') {
    fikaFAQs.push({
      question: "Can anyone attend Monthly Fika?",
      answer: "Yes! Monthly Fika is open to everyone - no membership required. It's the perfect way to experience our community and Swedish Fika tradition for the first time."
    });
  }

  if (eventType === 'weekly') {
    fikaFAQs.push({
      question: "How do I get access to Weekly Signature Fika?",
      answer: "Weekly Signature Fika is members-only. Join our community through email signup to get access to these intimate Tuesday gatherings with 20-25 global minds."
    });
  };

  return fikaFAQs;
};

// 9-29-25 additions
// Add these to useSEO.js - insert around line 200-350 near other FAQ exports

// ENHANCED: Curated Fika FAQs with AI-assisted matching emphasis
export const curatedFikaFAQs = [
  {
    question: "What is Curated Fika and how is it different from other Fika events?",
    answer: "Curated Fika uses AI-assisted matching to create perfect chemistry with just 3-4 compatible people for 90 minutes at premium venues. While Monthly Fika welcomes 40+ people and Signature Fika gathers 20-30 weekly, Curated Fika is for those seeking the deepest form of connection. Maximum chemistry, zero crowd."
  },
  {
    question: "How does AI-assisted matching work for Curated Fika?",
    answer: "Our data-driven compatibility analysis matches you with 3-4 people based on interests, conversation style, values, and connection goals. The algorithm ensures optimal group chemistry for meaningful conversations that go beyond small talk."
  },
  {
    question: "Why only 3-4 people instead of larger groups?",
    answer: "With 3-4 people, everyone can participate equally in one flowing conversation. There's no splitting into subgroups or competing for attention. This intimate size creates the perfect environment for authentic vulnerability and deeper connection—maximum chemistry, zero crowd."
  },
  {
    question: "What makes Curated Fika 'premium' and how much does it cost?",
    answer: "Curated Fika takes place at Tokyo's finest third-wave coffee venues with exceptional atmosphere. Currently in Beta, pricing details are shared during the application process. The focus is on quality over quantity—creating transformative connection experiences."
  },
  {
    question: "Is Curated Fika suitable for introverts or people with social anxiety?",
    answer: "Yes! The intimate 3-4 person format is ideal for introverts and those with social anxiety. Small groups feel less overwhelming, everyone's voice is heard, and the curated matching ensures compatible energy levels and communication styles."
  },
  {
    question: "How long is a Curated Fika session?",
    answer: "Each Curated Fika session lasts 90 minutes—the perfect duration for moving beyond small talk into meaningful connection without feeling rushed or exhausting. This timeframe allows for deep conversation while respecting everyone's schedules."
  }
];

// ENHANCED: Homepage happiness FAQ - update the existing one
export const homepageHappinessFAQs = [
  {
    question: "How does Connect & Co help people find happiness?",
    answer: "We create opportunities for authentic friendships through Swedish Fika tradition—whether you just arrived or lived here your whole life. Our complete offering includes Monthly Fika (free, 40+ people), Weekly Signature Fika (social rhythm), Curated Fika (3-4 matched people), Speed Fika Connection (structured deep questions), and diverse experiences that turn into lasting connections."
  },
  {
    question: "What is the difference between Monthly, Signature, and Curated Fika?",
    answer: "Monthly Fika is free and open to all with 40+ people—a low-pressure way to meet your first friends. Weekly Signature Fika is your social rhythm with 20-30 familiar faces. Curated Fika uses AI-assisted matching for 3-4 compatible people in premium venues—maximum chemistry, zero crowd. Each serves a different connection need."
  },
  {
    question: "Is Connect & Co only for newcomers to Japan?",
    answer: "No! Whether you just arrived or you've lived here your whole life, our community welcomes everyone. We have 1,000+ attendees across Tokyo, Osaka, and Fukuoka—both international members and locals who value authentic connection through Swedish Fika tradition."
  },
  {
    question: "What is Swedish Fika and why does it create happiness?",
    answer: "Swedish Fika is the practice of slowing down for meaningful conversation over coffee. It creates happiness by fostering authentic connection without agenda—no business cards, no rushing, just genuine presence. Research shows quality friendships are the strongest predictor of happiness."
  },
  {
    question: "How many people have attended Connect & Co events?",
    answer: "We've had 1,000+ attendees since February 2025 across 3 cities in Japan (Tokyo, Osaka, Fukuoka) and growing. Our community welcomes both newcomers and long-time residents who value building authentic friendships together."
  }
];

// ENHANCED: Curated Fika structured data with AI matching emphasis
export const curatedFikaStructuredData = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Curated Fika - AI-Assisted Intimate Swedish Coffee Experience",
  "alternateName": "Curated Fika Beta",
  "description": "AI-assisted matching creates perfect chemistry with 3-4 compatible people for 90-minute premium venue experiences. Maximum chemistry, zero crowd. The deepest form of Swedish Fika connection.",
  "url": "https://connectco.app/curated-fika",
  "provider": {
    "@type": "Organization",
    "name": "Connect & Co",
    "url": "https://connectco.app"
  },
  "areaServed": {
    "@type": "City",
    "name": "Tokyo",
    "addressCountry": "Japan"
  },
  "audience": {
    "@type": "Audience",
    "audienceType": "People seeking deepest form of authentic connection beyond small talk",
    "geographicArea": {
      "@type": "Place",
      "name": "Tokyo, Japan"
    }
  },
  "category": "Intimate Networking & AI-Assisted Social Matching",
  "serviceType": "Premium Small Group Coffee Experience",
  "offers": {
    "@type": "Offer",
    "name": "Curated Fika Beta Access",
    "description": "90-minute intimate coffee experience with 3-4 AI-matched compatible people at premium Tokyo venues",
    "availability": "https://schema.org/LimitedAvailability",
    "itemOffered": {
      "@type": "Event",
      "name": "Curated Fika Session",
      "duration": "PT1H30M",
      "maximumAttendeeCapacity": 4
    }
  },
  "features": [
    "AI-assisted compatibility matching",
    "3-4 person intimate groups",
    "90-minute sessions",
    "Premium Tokyo coffee venues",
    "Data-driven group chemistry",
    "Beyond small talk conversations"
  ]
};

// ENHANCED: Curated Fika FAQ Schema for structured data
export const curatedFikaFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "name": "Curated Fika FAQ - AI-Assisted Intimate Swedish Coffee",
  "url": "https://connectco.app/curated-fika#faq",
  "mainEntity": curatedFikaFAQs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

// ENHANCED: How-To Schema for Curated Fika experience
export const curatedFikaHowToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Experience Curated Fika - AI-Assisted Intimate Swedish Coffee",
  "description": "Step-by-step guide to joining an AI-assisted 3-4 person intimate Fika experience in Tokyo",
  "image": "https://connectco.app/curated-fika-how-to.jpg",
  "totalTime": "PT1H30M",
  "step": [
    {
      "@type": "HowToStep",
      "name": "Apply for Beta Access",
      "text": "Submit your application expressing interest in Curated Fika. Share your conversation style, interests, and what you're seeking in authentic connections.",
      "url": "https://connectco.app/curated-fika-signup"
    },
    {
      "@type": "HowToStep",
      "name": "Get AI-Matched with Compatible People",
      "text": "Our data-driven compatibility analysis matches you with 3-4 people based on shared interests, values, conversation style, and connection goals for optimal group chemistry."
    },
    {
      "@type": "HowToStep",
      "name": "Attend Your 90-Minute Premium Venue Session",
      "text": "Meet your matched group at a carefully selected premium Tokyo coffee venue. Enjoy 90 minutes of meaningful conversation that goes beyond small talk—maximum chemistry, zero crowd."
    },
    {
      "@type": "HowToStep",
      "name": "Build Lasting Authentic Connections",
      "text": "The intimate 3-4 person format creates space for vulnerability and depth. Many participants continue friendships long after their Curated Fika session ends."
    }
  ]
};

// ENHANCED: Comparison schema showing different Fika types
export const fikaOfferingsComparisonSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Connect & Co Fika Offerings",
  "description": "Complete range of Swedish Fika experiences from large open groups to intimate AI-matched gatherings",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "item": {
        "@type": "Event",
        "name": "Monthly Fika",
        "description": "Free, open to all. 40+ warm, welcoming people. Low-pressure way to meet your first friends.",
        "isAccessibleForFree": true,
        "maximumAttendeeCapacity": 40
      }
    },
    {
      "@type": "ListItem",
      "position": 2,
      "item": {
        "@type": "Event",
        "name": "Weekly Signature Fika",
        "description": "Your social rhythm. See familiar faces weekly and deepen new friendships. 20-30 exceptional people.",
        "maximumAttendeeCapacity": 30
      }
    },
    {
      "@type": "ListItem",
      "position": 3,
      "item": {
        "@type": "Event",
        "name": "Curated Fika",
        "description": "3-4 carefully matched people for 90 minutes. AI-assisted matching. Maximum chemistry, zero crowd.",
        "maximumAttendeeCapacity": 4,
        "duration": "PT1H30M"
      }
    },
    {
      "@type": "ListItem",
      "position": 4,
      "item": {
        "@type": "Event",
        "name": "Speed Fika Connection",
        "description": "Structured questions that skip small talk and spark real conversations.",
        "maximumAttendeeCapacity": 20
      }
    }
  ]
};

// Add this near the end of useSEO.js with other structured data exports
export const happinessPageStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "How to Be Happy: Authentic Friendships & Fika",
  "description": "Science-backed guide to finding happiness through authentic friendships and Swedish Fika tradition",
  "url": "https://connectco.app/happiness",
  "mainEntity": {
    "@type": "HowTo",
    "name": "How to Find Happiness Through Authentic Friendships",
    "description": "Three-step framework for building happiness through meaningful connections and Swedish Fika tradition",
    "image": "https://connectco.app/happiness-how-to.jpg",
    "step": [
      {
        "@type": "HowToStep",
        "name": "Connect",
        "text": "Start with one conversation over coffee through Swedish Fika tradition"
      },
      {
        "@type": "HowToStep", 
        "name": "Share",
        "text": "Be open, listen deeply, and let others in through authentic vulnerability"
      },
      {
        "@type": "HowToStep",
        "name": "Grow",
        "text": "Build friendships that last far beyond the table through consistent connection"
      }
    ]
  }
};

export const happinessFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the connection between friendship and happiness?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Harvard Study of Adult Development, spanning 85+ years, found that good relationships are the strongest predictor of happiness and health. Quality friendships keep us happier, healthier, and more resilient through life's challenges."
      }
    },
    {
      "@type": "Question",
      "name": "How does Swedish Fika create happiness?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Swedish Fika is the practice of slowing down for meaningful conversation over coffee. It creates happiness by fostering authentic connection, reducing social anxiety, and building the foundation for lasting friendships through intentional presence."
      }
    },
    {
      "@type": "Question",
      "name": "How can Connect & Co help me find happiness in Tokyo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Connect & Co creates opportunities for authentic connection through Swedish Fika traditions, curated small group experiences, and growth circles. Our community focuses on friendship-first approaches that naturally lead to increased happiness and wellbeing."
      }
    }
  ]
};